import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid,
  Typography, Avatar, Autocomplete, Stack, Tooltip, Modal, TextField, Divider, IconButton, InputAdornment, Chip, FormControlLabel,
  CircularProgress, ToggleButton, ToggleButtonGroup, MenuItem
} from '@mui/material';
import { makeStyles } from "@material-ui/core/styles";
import React, { FC, useEffect, useState, useRef } from 'react';
import { useAppDispatch, useAppSelector, } from "../../store/hooks";
import { StyledInput } from './Dialog.styles';
import greyDialogTitleSx from './greyDialogTitleSx';
import { partyFormSx, PartySection } from './partyFormStyles';
import { usePartyFormConfig } from '../../utils/partyFormConfig';
import FamilyMembers, { FamilyMember, toFamilyMembers, memberOfLabel } from './ClientAgent/FamilyMembers';
import { fetchAccountsByName, fetchBuyerOrSupplierByName, searchUnofyCrm, getUnofyCrmDetail, fetchCounterPartyAccountById } from "../../store/accountThunk";
import CustomTextField from '../FormFields/CustomTextField';
import CustomTextField1 from '../FormFields/CustomTextField1';
import Address from './ClientAgent/Address';
import Legal from './ClientAgent/Legal';
import Payment from './ClientAgent/Payment';
import Bank from './ClientAgent/Bank';
import Lead, { CrmLeadValue, fromPartyLead, isLeadComplete, toPartyLeadFields } from './ClientAgent/Lead';
import { FormProvider, useForm } from 'react-hook-form';
import { IAccountBasicFormInputs } from "../../utils/account.types";
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from "yup";
import { ApiStatus } from '../../utils/api.types';
import LoadingButton from '@mui/lab/LoadingButton';
import { addBuyerSellerAccount } from "../../store/accountThunk";
import AddIcon from '@mui/icons-material/Add';
import InfoIcon from '@mui/icons-material/Info';
import { fetchSuppliers } from '../../store/supplierStore/supplierThunk';
import { fetchCustomers } from '../../store/customerStore/customerThunk';
import HighlightOffOutlinedIcon from '@mui/icons-material/HighlightOffOutlined';
import HighlightOffTwoToneIcon from '@mui/icons-material/HighlightOffTwoTone';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import EmailChip from '../EmailChip/EmailChip';
import { toast } from 'react-toastify';
import DialogClientUpdateForm from './UpdateClientForm';
import useModuleAccess from '../../hooks/useModuleAccess';

interface DialogClientFormProps {
  title?: string;
  open: boolean;
  onClose: (k: any) => void;
  onCancel: () => void;
  selectedData: (data: any) => void;
  /** Address of the voucher's branch, used when the CRM lead has none of its own. */
  locationAddress?: any;
}

// Country and state are seeded by the form itself, so only the typed fields count
// as evidence that an address was actually supplied.
const ADDRESS_FIELDS = ['Line1', 'Line2', 'City', 'PinCode'];

const hasAnyAddress = (values: any) =>
  ['billingAddress', 'shippingAddress'].some((prefix) =>
    ADDRESS_FIELDS.some((f) => String(values?.[`${prefix}${f}`] || '').trim() !== '')
  );

const style = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  transition: '0ms',
  width: 1000,
  borderRadius: 2,
  bgcolor: 'background.paper',
  border: 'none',
  boxShadow: 24,
};

const useStyles = makeStyles({
  paper: {
    border: "1.5px solid #e9ecf1",
    boxShadow: 'none',
    borderRadius: 7
  },
  toggleButtonGroup: {
    width: '100%',
  },
  toggleButton: {
    width: 15,
    height: 15,
    marginBottom: 2
  },

});

const DialogClientForm: FC<DialogClientFormProps> = ({ title, onClose, onCancel, open, selectedData, locationAddress, children }) => {
  // On when the CRM sent no address at all: the branch address stands in, shown as
  // placeholders, so the required-address rules would block a form the user need
  // not fill. Any address data from either side turns this off.
  const [useLocationAddress, setUseLocationAddress] = useState(false);
  const addressRule = (message: string) =>
    useLocationAddress ? yup.string().notRequired() : yup.string().required(message);

  // Schema to validate account form.
  const schema = yup.object().shape({
    name: yup.string().required("Name is required."),
    legalName: yup.string().required("Legal Name is required."),
    billingAddressCountry: addressRule("Billing Address Country is required."),
    billingAddressCity: addressRule("Billing Address City is required."),
    shippingAddressCountry: addressRule("Shipping Address Country is required."),
    shippingAddressCity: addressRule("Shipping Address City is required."),
    // registrationNo: yup.string().required("Registration is required."),
    // countryOfRegistration: yup.string().required("Country of registration is required."),
    // stateOfRegistration: yup.string().required("State of registration is required."),
    // taxIdentificationNumber1: yup.string().required("Tax Identification #1 (PAN for India) is required."),
    // taxIdentificationNumber2: yup.string().required("Tax Identification #2 (GST for India) is required."),
    // upiId: (title === 'Supplier') ? yup.string().required("UPI ID is required.") : yup.string().notRequired(),
    // gPayPhone: (title === 'Supplier') ? yup.string().required("GPay number is required.") : yup.string().notRequired(),
    // bankName: (title === 'Supplier') ? yup.string().required("Bank name is required.") : yup.string().notRequired(),
    // branchName: (title === 'Supplier') ? yup.string().required("Branch name is required.") : yup.string().notRequired(),
    // IFSC: (title === 'Supplier') ? yup.string().required("IFSC is required.") : yup.string().notRequired(),
    currency: (title === 'Supplier') ? yup.string().required("Supplier is required.") : yup.string().notRequired(),
    // accountNumber: (title === 'Supplier') ? yup.string().required("Account number is required.") : yup.string().notRequired(),
    // accountName: (title === 'Supplier') ? yup.string().required("Account name is required.") : yup.string().notRequired()
  });

  const dispatch = useAppDispatch();
  const classes = useStyles();
  const [image, setImage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [options, setOptions] = useState<any[]>([]);
  const [selected, setSelected] = useState<any>();
  const [newOption, setNewOption] = useState("");
  // MUI Select needs its own value; register() alone won't display it.
  const [gender, setGender] = useState("");
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  // Family member the document is for ('' = the buyer themself); handed back on Select.
  const [invoiceForId, setInvoiceForId] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [saveButton, setSaveButton] = useState('Select');
  const { selectedAccount } = useAppSelector(state => state.auth);
  const elmRef = useRef<any>(null);
  const [mailerList, setMailerList] = useState<any>();
  const [isClientEdit, setIsClientEdit] = useState<any>();
  // Edit the selected buyer / supplier in place, without leaving the voucher form.
  const [openEditParty, setOpenEditParty] = useState(false);
  const { can, planAllowsModule } = useModuleAccess();
  const canEditParty = can(title === 'Supplier' ? 'suppliers' : 'buyers', 'edit');
  // Reload the edited party so the fields (and the voucher) get the saved values.
  const reloadEditedParty = async (name: string) => {
    const id = selected?._id;
    if (!id || !name) return;
    try {
      const data: any = await dispatch(fetchBuyerOrSupplierByName(`name=${encodeURIComponent(name)}&&searchFor=${title?.toLocaleLowerCase()}&limit=10`)).unwrap();
      const fresh = (data?.accounts || []).find((a: any) => a._id === id);
      // Remount the form sections (as on first pick) so the reset in the selected effect doesn't wipe them.
      if (fresh) { setIsLoading(true); setSelected(fresh); setCrmLead(fromPartyLead(fresh.crmLead)); }
    } catch (error) {
      console.error('Error reloading edited party:', error);
    }
  };
  
  const [searchTerm, setSearchTerm] = useState('');   // Track user input
  const [debouncedTerm, setDebouncedTerm] = useState(searchTerm); // Debounced value
  const TIME_DELAY = 500;

  // Unofy CRM buyer import. Only offered for Buyers on accounts with the flag on.
  const crmEnabled = title === 'Buyer' && !!selectedAccount?.unofyCrm?.enabled;
  const [searchSource, setSearchSource] = useState<'wareozo' | 'unofy'>('wareozo');
  // Provenance of a CRM-imported buyer, carried into the create payload.
  const [crmImport, setCrmImport] = useState<{
    leadId: string; source: string; sourceName: string; subSourceName: string; agentName: string;
  } | null>(null);
  // Lead attribution picked in the Lead section; Unofy CRM accounts only.
  const [crmLead, setCrmLead] = useState<CrmLeadValue>({});
  const [leadSubmitted, setLeadSubmitted] = useState(false);

  // use form
  const methods = useForm<IAccountBasicFormInputs>({
    resolver: yupResolver(schema),
  });

  // Admin-configured hidden fields (Form Configuration drawer); refreshed each time the dialog opens.
  const { config: partyForm, reload: reloadPartyForm, isHidden } = usePartyFormConfig(title === 'Supplier' ? 'supplier' : 'buyer');
  useEffect(() => { if (open) reloadPartyForm(); }, [open]);
  const hide = (field: string) => (isHidden(field) ? 'none' : undefined);
  // Shipping section hidden: save the billing address as shipping too.
  const copyBillingToShippingIfHidden = () => {
    if (!isHidden('shippingAddress')) return;
    ['Country', 'City', 'State', 'Line1', 'Line2', 'PinCode'].forEach((f) =>
      methods.setValue(`shippingAddress${f}` as any, methods.getValues(`billingAddress${f}` as any)));
  };
  const formRef = useRef<any>();

  const { status } = useAppSelector(state => state.account);

  // Method to set preview of selected picture.
  const handleSelectFile = (event: any) => {
    const target = event.target as HTMLInputElement;

    if (target.files && target.files[0]) {
      setSelectedFile(target.files[0]);
      setImage(URL.createObjectURL(target.files[0]));
    }
  };
  const onChangeHandler = (event: any, value: any) => {
    console.log("onChangeHandler called", value);
    if (value && value.__crm) {
      importCrmLead(value);
      return;
    }
    setNewOption('');
    setCrmLead(fromPartyLead(value?.crmLead));
    // setIsLoading(true);
    setSelected(value);
    setIsClientEdit(false);
    setUseLocationAddress(false);
    // setIsLoading(false);
  }

  // CRM provenance lives on the invoice, not on the party record, so it travels
  // on the buyer object handed back to the invoice form.
  const withCrmProvenance = (buyer: any) => {
    if (!buyer || !crmEnabled) return buyer;
    if (!crmImport) return { ...buyer, crmLead };
    return {
      ...buyer,
      crmLead,
      crmLeadId: crmImport.leadId,
      crmSource: crmImport.source,
      crmSourceName: crmImport.sourceName,
      crmSubSourceName: crmImport.subSourceName,
      crmAgentName: crmImport.agentName,
    };
  };

  const importCrmLead = async (row: any) => {
    const leadId = row.leadId;
    setCrmLead({});
    try {
      setIsLoading(true);
      const res: any = await dispatch(getUnofyCrmDetail(leadId)).unwrap();
      const form = res?.buyerForm || {};

      // The attribution comes off the search row — the detail call doesn't return it.
      setCrmImport({
        leadId: form.leadId || leadId,
        source: form.source || '',
        sourceName: row.source_name || '',
        subSourceName: row.sub_source_name || '',
        agentName: row.agent_name || '',
      });

      // Already a buyer here: load it the way a Wareozo pick does, so the user
      // gets Select instead of Create and no duplicate party is made.
      if (res?.existingBuyer) {
        setNewOption('');
        setIsClientEdit(false);
        setSaveButton('Select');
        setCrmLead(fromPartyLead(res.existingBuyer.crmLead));
        setSelected(res.existingBuyer);
        setUseLocationAddress(false);
        return;
      }

      // Fresh lead: enter "Add New" (create) mode without a `selected`.
      setSelected('');
      setIsClientEdit(true);
      setSaveButton('Create');
      setNewOption('new');

      // Prefill the form. reset() first so stale values are cleared, then apply.
      methods.reset(form as any);
      // A CRM DOB always wins — a CRM age goes stale as time passes.
      const crmDob = form.dateOfBirth || '';
      methods.setValue('dateOfBirth', crmDob);
      methods.setValue('age', calculateAge(crmDob) || (form.age ?? ''));
      // The Gender select is controlled by local state, so reset() alone won't show it.
      methods.setValue('gender', form.gender || '');
      setGender(form.gender || '');
      // EmailChip reads its initial value from mailerList; seed it from the CRM emails.
      setMailerList(Array.isArray(form.email) ? form.email : (form.email ? [form.email] : []));
      setUseLocationAddress(!hasAnyAddress(form) && !!locationAddress);
    } catch (error) {
      console.error('Error importing CRM lead:', error);
    } finally {
      setIsLoading(false);
    }
  };
  // Update the debounced value after a delay (debouncing the user input)
  useEffect(() => {
    if (selected && searchTerm === selected.name) return; // picked buyer's name, not typing
    setIsLoading(true);
    const timerId = setTimeout(() => {
      setDebouncedTerm(searchTerm.trim().length >= 3 ? searchTerm : ''); // search from 3 chars
      setIsLoading(false);
    }, 500);  // 500ms delay
    
    return () => clearTimeout(timerId); // Cleanup previous timer on new keypress
  }, [searchTerm]);

  // Make API call when debouncedTerm changes (user has stopped typing)
  useEffect(() => {
    setIsLoading(true);
    const fetchData = async () => {
      try {
        if (crmEnabled && searchSource === 'unofy') {
          // Unofy CRM lead search. Requires a term (CRM has no "empty = recent" mode).
          if (!debouncedTerm) {
            setOptions([]);
          } else {
            const qs = `q=${encodeURIComponent(debouncedTerm)}&type=lead&page=1&limit=20`;
            await dispatch(searchUnofyCrm(qs)).unwrap().then((data) => {
              const rows = (data && data.data) ? data.data : [];
              // Shape rows so getOptionLabel/renderOption (which read option.name) work.
              setOptions(rows.map((r: any) => ({
                ...r,
                name: r.name || r.phone || r.leadId,
                __crm: true,
              })));
            });
          }
        } else {
          const _querystring = `name=${debouncedTerm}&&searchFor=${title?.toLocaleLowerCase()}&limit=10`
          await dispatch(fetchBuyerOrSupplierByName(_querystring)).unwrap().then((data) => {
            if (data) {
              const d = data.accounts.filter(function (item) {
                return item._id !== selectedAccount._id;
              });

              // When no search term, show first 5 results
              setOptions(debouncedTerm ? d : d.slice(0, 5));
            }
          });
        }
      } catch (error) {
        console.error("Error fetching search results:", error);
        setOptions([]);
      }
      setTimeout(() => {setIsLoading(false)}, TIME_DELAY);
    };

    fetchData();
  }, [debouncedTerm, searchSource]); // Run when debouncedTerm or source changes

  const onInputChangeHandler = async (event: React.SyntheticEvent, value: string) => {
    setSearchTerm(value);
  };

  // const onInputChangeHandler = async (event: React.SyntheticEvent, value: string) => {
  //   setSelected('');
  //   setNewOption('');
  //   const { _id: id } = selectedAccount;
  //   // let option = [];
  //   // const data: any = await dispatch(fetchCustomers(id));
  //   // console.log(data)

  //   // if (data.payload.length > 0) {
      
  //   //   option = data.payload.map((item: any) => {
  //   //     console.log(item);
  //   //     return { label: item.name, value: item._id }
  //   //   });
  //   // }
  //   // console.log("setting up options");
  //   // console.log(option);
  //   // setOptions(option);


  //   // await dispatch(fetchCustomers(id)).unwrap().then((data) => {
  //   //   console.log(data)
  //   //   if (data) {
  //   //     console.log("checking customer data");
  //   //     console.log(data.customers);
  //   //     let option = [];
  //   //     option = data.customers.filter((item: any) => {
  //   //       console.log(item);
  //   //       return { label: item.name, value: item._id }
  //   //     });

  //   //     console.log(option);

  //   //     setOptions(option);

  //   //     // const d = data.customers.filter(function (item) {
  //   //     //   return item._id !== selectedAccount._id;
  //   //     // });

  //       setOptions(option);
  //   //   }
  //   // });
  // // };

  // const onInputChangeHandler = async (event: React.SyntheticEvent, value: string) => {
  //   setSelected('');
  //   setNewOption('');
  //   console.log("input changed called");
  //   if (selectedAccount) {
  //     const { _id: id } = selectedAccount;
  //     const data: any = await dispatch(fetchCustomers(id));
  //     console.log(data);

  //     if (data.payload.length > 0) {
  //       let option = [];
  //       option = data.payload.map((item: any) => {
  //         console.log(item);
  //         return { label: item.name, value: item._id }
  //       });
  //       setOptions(option);
  //     }
  //   }
  // };

  const onAddNew = () => {
    if (elmRef) {
      const ele = elmRef?.current.getElementsByClassName('MuiAutocomplete-clearIndicator')[0];
      if (ele) ele.click();
    }
    // Manual add is not a CRM import — clear any prior CRM prefill provenance.
    setCrmImport(null);
    setCrmLead({});
    setUseLocationAddress(false);
    setIsClientEdit(true);
    setSelected('')
    setNewOption('');
    setSaveButton('Create')
    setNewOption('new');
  };

  useEffect(() => {
    // A CRM import prefills the form itself (importCrmLead) — don't wipe it here.
    if (crmImport && newOption === 'new') {
      setIsLoading(false);
      return;
    }
    // <Address> fills its fields before this runs (child effects first); keep them for a picked party.
    const keptAddress = selected ? Object.entries(methods.getValues() as any)
      .filter(([k]) => k.startsWith('billingAddress') || k.startsWith('shippingAddress')) : [];
    methods.reset();
    keptAddress.forEach(([k, v]) => methods.setValue(k as any, v));
    if (selected) {
      methods.setValue("name", selected.name);
      setInvoiceForId('');
      methods.setValue("legalName", selected.legalName);
      methods.setValue("website", selected.website);
      methods.setValue("contactName", selected.contactName);
      methods.setValue("email", selected.email);
      methods.setValue("whatsAppNumber", selected.whatsAppNumber);
      if (title == 'Buyer') {
        // <input type="date"> needs a YYYY-MM-DD value.
        const dob = (selected as any).dateOfBirth ? String((selected as any).dateOfBirth).slice(0, 10) : '';
        methods.setValue("dateOfBirth", dob);
        // A stored DOB always wins — a saved age goes stale as time passes.
        methods.setValue("age", calculateAge(dob) || ((selected as any).age ?? ''));
        methods.setValue("gender", (selected as any).gender || '');
        setGender((selected as any).gender || '');
      }
      setIsLoading(false);
    } else if (newOption) {
      setGender('');
      setIsLoading(false);
    }
  }, [selected, newOption, methods, isClientEdit, crmImport]);

  // Completed years from a YYYY-MM-DD string; '' when it isn't a usable past date.
  const calculateAge = (value: string): number | '' => {
    if (!value) return '';
    const dob = new Date(value);
    if (isNaN(dob.getTime())) return '';
    const today = new Date();
    let years = today.getFullYear() - dob.getFullYear();
    const monthDiff = today.getMonth() - dob.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) years--;
    return years >= 0 ? years : '';
  };

  // Family members come populated on the party; reset when it's opened, picked or reloaded after an edit.
  useEffect(() => {
    if (open) setFamilyMembers(toFamilyMembers((selected as any)?.familyMembers || []));
  }, [open, selected]);

  // DOB drives age; age stays editable for parties with no DOB.
  const handleDobChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const value = event.target.value;
    methods.setValue('dateOfBirth', value);
    const years = calculateAge(value);
    if (years !== '') methods.setValue('age', years);
  };

  const getFormData = (object: any) => {
    const formData = new FormData();
    // FormData would send an unset value as the text "undefined".
    Object.keys(object).forEach(key => formData.append(key, object[key] ?? ''));
    return formData;
  };

  const createClient = async (e: any) => {
    if (selectedFile) {
      e.append('logo', selectedFile);
    }

    e.delete('email');
    e.append('email', JSON.stringify(mailerList));
    e.delete('familyMembers');
    e.append('familyMembers', JSON.stringify(familyMembers.filter((m) => m.name.trim())));

    const resData: any = await dispatch(addBuyerSellerAccount(e));

    // Surface any backend rejection (e.g. duplicate name/PAN/GST) and keep the
    // dialog open so the user can correct or search for the existing party.
    if (resData.error) {
      toast.error(resData.payload?.message || 'Failed to create buyer.');
      return;
    }

    if (resData.payload) {
      const buyer = resData.payload.fullAccountDetails;
      // Attach the external CRM provenance to the buyer handed back to the
      // invoice, so it can be stored on the invoice (not on the party record).
      setSelected(buyer);
      selectedData(withCrmProvenance(buyer));
      setCrmLead({});
      onClose(true);
      methods.reset();
    }
  };

  const handleRenderOption = (e: any, b: any, c: any) => {
    // console.log('b.counterPartyOfAccount: ', b.counterPartyOfAccount, b.name);
    return isLoading ? <div style={{ 'marginLeft': '20px' }}><CircularProgress color="info" size={20}/></div> : <div {...e} className='auto-item' key={Math.random()}>
      <div className='img-wrapper'>
        <Avatar
          alt={b.name}
          imgProps={{ referrerPolicy: "no-referrer" }}
          src={b.logo}
          variant='square'
          style={{
            width: "30px",
            height: "30px",
            objectFit: "contain",
          }}
          sizes={"xs"}
        >{b.name.toString().substr(0, 1).toUpperCase()}</Avatar>
      </div>
      <div><b style={{ 'marginRight': '4px' }}>
        {b.name}
      </b>
        {b.__crm
          ? <>
              {/* One person can hold several leads, so the row has to be identifiable. */}
              <span style={{ fontSize: '11px', color: '#488dff', fontWeight: 600 }}>{b.leadId}</span>
              <div style={{ fontSize: '11px', color: '#888' }}>
                {[b.phone, b.lead_age, b.facility_name, b.sub_source_name, b.agent_name,
                  b.createdAt ? new Date(b.createdAt).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : ''
                ].filter(Boolean).join(' • ')}
              </div>
            </>
          : <>
              {!b.counterPartyOfAccount && <Tooltip title="Verified account"><InfoIcon style={{ fontSize: '12px', color: '#09b2ee' }}></InfoIcon></Tooltip>}
              {b.counterPartyOfAccount && <Tooltip title="My customer / vendor"><InfoIcon style={{ fontSize: '12px', color: '#aaa' }}></InfoIcon></Tooltip>}
              {memberOfLabel(b) && <span style={{ fontSize: 11, color: '#488dff', fontWeight: 600, marginLeft: 6 }}>{memberOfLabel(b)}</span>}
              {b.whatsAppNumber && <div style={{ fontSize: 12, color: '#64748b' }}>{b.whatsAppNumber}</div>}
            </>
        }
      </div>
    </div>
  };

  const selectedEmailDataHandler = (data: any) => {
    console.log("====> selectedEmailDataHandler called from updateClientForm  ====> ");
    setMailerList(data);
    console.log(mailerList);
  };

  const onSubmit = (e: any) => {

    const data = methods.getValues();
    data.buyerSellerAgent = title;
    data.email = mailerList;

    // The placeholders were only a hint; nothing typed means the branch address is
    // what gets saved. One field typed and the user owns the whole address.
    if (useLocationAddress && locationAddress && !hasAnyAddress(data)) {
      ['billingAddress', 'shippingAddress'].forEach((prefix) => {
        (data as any)[`${prefix}Line1`] = locationAddress.line1 || '';
        (data as any)[`${prefix}Line2`] = locationAddress.line2 || '';
        (data as any)[`${prefix}City`] = locationAddress.city || '';
        (data as any)[`${prefix}State`] = locationAddress.state?._id || locationAddress.state || '';
        (data as any)[`${prefix}Country`] = locationAddress.country?._id || locationAddress.country || '';
        (data as any)[`${prefix}PinCode`] = locationAddress.code || '';
      });
    }
    if (crmEnabled && planAllowsModule('crm') && !selected && !isLeadComplete(crmLead)) return;
    // A new buyer keeps the lead it was created with.
    if (crmEnabled) Object.assign(data, toPartyLeadFields(crmLead));
    console.log(data);

    delete e.search;
    e.buyerSellerAgent = title;

    let form = getFormData(data);
    createClient(form);
  };

  const section = (heading: string) => <PartySection heading={heading} />;
  const readOnly = !!selected;
  const field = (name: any, label: string, placeholder: string, extra: any = {}) => (
    <>
      <h5 className='c-label'>{label}</h5>
      <TextField
        placeholder={placeholder}
        className='c-text-field'
        {...methods.register(name)}
        variant="standard"
        sx={{ width: '100%' }}
        size='small'
        InputProps={{ disableUnderline: true, readOnly }}
        {...extra}
      />
      <p style={{ color: "#c33b3b", fontSize: 12, margin: '4px 0 0 0' }}>{(methods.formState.errors as any)[name]?.message ?? ""}</p>
    </>
  );
  // Full Name fills Legal/Business Name until the user types a different legal name.
  const nameRegister = methods.register('name');
  const onFullNameChange = (e: any) => {
    const prevName = methods.getValues('name') || '';
    const legalName = methods.getValues('legalName') || '';
    nameRegister.onChange(e);
    if (!legalName || legalName === prevName) methods.setValue('legalName', e.target.value, { shouldValidate: true });
  };
  const contactFields = ['email', 'whatsAppNumber', ...(title == 'Buyer' ? ['dateOfBirth', 'age', 'gender'] : [])];
  const showContact = contactFields.some((f) => !isHidden(f));

  const handleCancelClick = () => {
    methods.reset();
    setCrmLead({});
    setSelected('');
    setNewOption('');
    onCancel();
    onClose(false);
  };

  const namedMembers = familyMembers.filter((m) => m._id && m.name.trim());
  const showInvoiceFor = title == 'Buyer' && !!selected?._id && namedMembers.length > 0;

  const handleSelectClick = async () => {
    const member = namedMembers.find((m) => m._id === invoiceForId) || null;
    // Member with its own party: bill that party. Older members without one keep the snapshot.
    const memberParty = member?.counterParty ? (await dispatch(fetchCounterPartyAccountById(member.counterParty))).payload : null;
    methods.reset();
    selectedData(memberParty ? withCrmProvenance(memberParty) : { ...withCrmProvenance(selected), _familyMember: member });
    setInvoiceForId('');
    setCrmLead({});
    setSelected('');
    setNewOption('');
    setSaveButton('Select');
    onClose(false);
  };

  return (
    <div className="drawer-form-container">
      <Dialog
        open={open}
        onClose={(event: any, reason: any) => { if (reason !== 'backdropClick') onClose(false); }}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { width: '100%', maxWidth: '1100px', height: '95vh', maxHeight: '95vh', m: { xs: 1, sm: 2 } } }}
      >
      <FormProvider {...methods}>
        <Box
          component="form"
          onSubmit={(e) => { setLeadSubmitted(true); copyBillingToShippingIfHidden(); methods.handleSubmit(data => onSubmit(data))(e); }}
          ref={formRef}
          noValidate
          sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}
        >
          <DialogTitle sx={{ ...greyDialogTitleSx, display: 'flex', alignItems: 'center', gap: 1, py: 1.25, flexWrap: 'wrap' }}>
            <Typography variant="h5" sx={{ flex: 1, minWidth: 0 }}>{newOption == 'new' && !selected ? `Add New ${title}` : `Search ${title}`}</Typography>
            <Box sx={{ display: 'flex', gap: 1, '& .MuiButton-root': { height: 34, textTransform: 'none' } }}>
              <Button variant="outlined" onClick={handleCancelClick}>Cancel</Button>
              {selected && selected._id && canEditParty &&
                <Button variant="outlined" onClick={() => setOpenEditParty(true)}>Edit</Button>}
              {selected &&
                <Button variant="contained" onClick={handleSelectClick}>Select</Button>}
              {newOption == 'new' && !selected &&
                <LoadingButton type="submit" name={'submit'} loading={status === ApiStatus.LOADING} variant="contained">
                  {saveButton}
                </LoadingButton>}
            </Box>
          </DialogTitle>

          <DialogContent sx={{ flex: 1, overflow: 'auto', px: { xs: 2, sm: 3 }, pt: '12px !important', pb: 2, ...partyFormSx }}>
            <Grid container columnSpacing={2} rowSpacing={1}>
              <Grid item xs={12} md={showInvoiceFor ? 8 : 12} className={showInvoiceFor ? undefined : 'full-row'}>
                {crmEnabled &&
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={searchSource}
                    onChange={(_e, val) => {
                      if (!val) return;
                      setSearchSource(val);
                      // Reset search context when switching source.
                      setSearchTerm('');
                      setDebouncedTerm('');
                      setOptions([]);
                      setSelected('');
                      setNewOption('');
                      setCrmImport(null);
                      setCrmLead({});
                    }}
                    sx={{ mb: 1 }}
                  >
                    <ToggleButton value="wareozo" style={{ textTransform: 'none' }}>Wareozo</ToggleButton>
                    <ToggleButton value="unofy" style={{ textTransform: 'none' }}>Unofy CRM</ToggleButton>
                  </ToggleButtonGroup>
                }
                <h5 className='c-label'>{searchSource === 'unofy' ? 'Search Unofy CRM (lead)' : `Search ${title}`}</h5>
                <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: { xs: 'wrap', sm: 'nowrap' } }}>
                  <Autocomplete
                    ref={elmRef}
                    id={`search ${title}`}
                    disablePortal
                    onChange={onChangeHandler}
                    inputValue={searchTerm}
                    onInputChange={onInputChangeHandler}
                    options={options}
                    filterOptions={(x) => x}
                    renderOption={handleRenderOption}
                    classes={{ paper: classes.paper }}
                    sx={{ flex: 1, minWidth: 0, width: '100%' }}
                    getOptionLabel={option => option.name || ''}
                    renderInput={(params) => <TextField {...params}
                      placeholder={searchSource === 'unofy' ? 'Search CRM by name or phone' : `Type here to Search ${title}`}
                      required
                      className='c-text-field'
                      variant="standard"
                      size='small'
                      InputProps={{ ...params.InputProps, disableUnderline: true }}
                    />}
                  />
                  {!selected &&
                    <Button variant='outlined' startIcon={<AddIcon />} onClick={onAddNew} sx={{ height: 35, mt: 0.5, whiteSpace: 'nowrap', textTransform: 'none', flexShrink: 0 }}>
                      Add New {title}
                    </Button>
                  }
                </Box>
                {memberOfLabel(selected) &&
                  <Typography variant="body2" sx={{ mt: 0.5, color: '#488dff', fontWeight: 600 }}>{memberOfLabel(selected)}</Typography>}
              </Grid>

              {showInvoiceFor &&
                <Grid item xs={12} md={4} sx={{ alignSelf: 'flex-end' }}>
                  <h5 className='c-label'>Invoice For</h5>
                  <TextField
                    select
                    className='c-text-field'
                    variant="standard"
                    size='small'
                    sx={{ width: '100%' }}
                    value={invoiceForId}
                    onChange={(e) => setInvoiceForId(e.target.value)}
                    InputProps={{ disableUnderline: true }}
                    SelectProps={{ displayEmpty: true }}
                  >
                    <MenuItem value=''>{selected.name} (Self)</MenuItem>
                    {namedMembers.map((m: any) => (
                      <MenuItem key={m._id} value={m._id}>{m.name}{m.relation ? ` (${m.relation})` : ''}</MenuItem>
                    ))}
                  </TextField>
                </Grid>}

              {isLoading ? <div></div> : (selected || (newOption)) && (
                <>
                  <Grid item xs={12} className="full-row" sx={{ mt: 1 }} />

                  {partyForm.gstAutofillTop && <Legal selected={selected} methods={methods} part="gstLookup" />}

                  <Grid item xs={12} md={2} className="full-row" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: '20px !important', pb: 1 }}>
                    <Box sx={{ position: 'relative' }}>
                      <Avatar
                        alt={"Picture"}
                        imgProps={{ referrerPolicy: "no-referrer" }}
                        src={(selected && selected.logo) ? selected.logo : image}
                        sx={{ width: 120, height: 96, objectFit: 'contain', border: '1px dashed #9ca3af', bgcolor: '#f5f6f8', color: '#9ca3af', '& .MuiAvatar-fallback': { width: '60%', height: '60%' } }}
                        variant='rounded'
                      />
                      {!selected && <label htmlFor="logo" title="Choose logo">
                        <StyledInput accept="image/*" id="logo" type="file" onChange={handleSelectFile} />
                        <Box sx={{ position: 'absolute', right: -10, bottom: -10, width: 32, height: 32, borderRadius: '50%', bgcolor: 'primary.main', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', border: '2px solid #fff', boxShadow: 1 }}>
                          <AddIcon sx={{ fontSize: 20 }} />
                        </Box>
                      </label>}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={10} className="full-row">
                    <Grid container columnSpacing={2} rowSpacing={1}>
                      <Grid item xs={12} sm={6}>{field('name', 'Full Name *', 'Full Name', { onChange: onFullNameChange })}</Grid>
                      <Grid item xs={12} sm={6}>{field('legalName', 'Legal/Business Name *', 'Legal/Business Name')}</Grid>
                      <Grid item xs={12} sm={6} sx={{ display: hide('website') }}>{field('website', 'Website', 'Website')}</Grid>
                      <Grid item xs={12} sm={6} sx={{ display: hide('contactName') }}>{field('contactName', 'Contact Name', 'Contact Name')}</Grid>
                    </Grid>
                  </Grid>

                  {showContact && section('Contact Information')}
                  <Grid item xs={12} md={title == 'Buyer' ? 4 : 6} sx={{ display: hide('email') }}>
                    <EmailChip buyerEmail={selected ? selected.email : mailerList} methods={methods} selectedEmailData={selectedEmailDataHandler} isEdit={isClientEdit}/>
                  </Grid>
                  <Grid item xs={12} md={title == 'Buyer' ? 2.5 : 6} sx={{ display: hide('whatsAppNumber') }}>{field('whatsAppNumber', 'WhatsApp Number', 'WhatsApp Number', { type: 'number' })}</Grid>
                  {title == 'Buyer' && <>
                    <Grid item xs={12} sm={6} md={2} sx={{ display: hide('dateOfBirth') }}>
                      {field('dateOfBirth', 'Date of Birth', '', { type: 'date', onChange: handleDobChange })}
                    </Grid>
                    <Grid item xs={12} sm={6} md={1.5} sx={{ display: hide('age') }}>{field('age', 'Age', 'Age', { type: 'number' })}</Grid>
                    <Grid item xs={12} sm={6} md={2} sx={{ display: hide('gender') }}>
                      <h5 className='c-label'>Gender</h5>
                      <TextField
                        select
                        className='c-text-field'
                        {...methods.register('gender')}
                        value={gender}
                        onChange={(event) => {
                          setGender(event.target.value);
                          methods.setValue('gender', event.target.value);
                        }}
                        variant="standard"
                        sx={{ width: '100%' }}
                        size='small'
                        InputProps={{ disableUnderline: true, readOnly }}>
                        <MenuItem value=''>Select Gender</MenuItem>
                        <MenuItem value='Male'>Male</MenuItem>
                        <MenuItem value='Female'>Female</MenuItem>
                        <MenuItem value='Other'>Other</MenuItem>
                      </TextField>
                    </Grid>
                  </>}

                  <Address selected={selected} methods={methods} fallbackAddress={useLocationAddress ? locationAddress : null} hideShipping={isHidden('shippingAddress')} stacked />

                  {title == 'Supplier' && !isHidden('bankDetails') && section('Bank Details')}
                  {title == 'Supplier' &&
                    <Grid item xs={12} className="full-row" sx={{ display: hide('bankDetails') }}>
                      <Grid container columnSpacing={2.5} rowSpacing={1.5}>
                        <Bank selected={selected} methods={methods} />
                      </Grid>
                    </Grid>
                  }

                  {crmEnabled &&
                    <Lead
                      // Remount per buyer / lead so the CRM labels re-seed.
                      key={crmImport?.leadId || selected?._id || 'new'}
                      value={crmLead}
                      defaults={crmImport ? {
                        leadId: crmImport.leadId,
                        sourceName: crmImport.sourceName,
                        subSourceName: crmImport.subSourceName,
                        agentName: crmImport.agentName,
                      } : undefined}
                      onChange={setCrmLead}
                      readOnly={!!selected}
                      showErrors={leadSubmitted} />}

                  {!isHidden('familyMembers') &&
                    <FamilyMembers value={familyMembers} onChange={setFamilyMembers} readOnly={!!selected} />}

                  <Legal selected={selected} methods={methods} hiddenFields={partyForm.hiddenFields} part={partyForm.gstAutofillTop ? "withoutGst" : "all"} />
                </>
              )}
            </Grid>
          </DialogContent>

        </Box>
      </FormProvider>
      </Dialog>
      {openEditParty && (
        <DialogClientUpdateForm
          title={title}
          open={openEditParty}
          onClose={() => setOpenEditParty(false)}
          onCancel={() => setOpenEditParty(false)}
          selectedData={selected}
          isClientEdit={true}
          onSaved={reloadEditedParty}
        />
      )}
    </div >
    
  );
};

export default DialogClientForm;
